import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Box, Typography } from "@mui/material";
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import {
  ADMIN_REFETCH_INTERVAL,
  getFirebaseClient,
  useAuth,
  StatCard,
} from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi } from "@/lib/callables";

function dateRanges() {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(todayStart.getDate() - todayStart.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    todayTs: Timestamp.fromDate(todayStart),
    weekTs: Timestamp.fromDate(weekStart),
    monthTs: Timestamp.fromDate(monthStart),
    monthStartStr: monthStart.toISOString().split("T")[0],
    todayStr: todayStart.toISOString().split("T")[0],
  };
}

function isThisWeek(dateStr: string, todayStr: string): boolean {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date(todayStr + "T00:00:00");
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  return d >= weekStart && d <= today;
}

export default function DashboardPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const { db } = getFirebaseClient();
  const { todayTs, weekTs, monthTs, monthStartStr, todayStr } = useMemo(dateRanges, []);

  const pharmaciesQ = useQuery({
    queryKey: ["chain", chainId, "pharmacies"],
    queryFn: async () => {
      const snap = await getDocs(
        query(collection(db, "chains", chainId, "pharmacies"), orderBy("displayName")),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        status: String((d.data() as Record<string, unknown>).status ?? "active"),
      }));
    },
    enabled: !!chainId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const pharmacistsQ = useQuery({
    queryKey: ["chain", chainId, "memberships", "all"],
    queryFn: () => chainAdminApi.listMemberships({ chainId, status: "all" }),
    enabled: !!chainId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const consultationsQ = useQuery({
    queryKey: ["chain", chainId, "consultations", "dashboard", monthTs.toMillis()],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "consultations"),
          where("chainId", "==", chainId),
          where("createdAt", ">=", monthTs),
        ),
      );
      return snap.docs.map((d) => ({
        createdAt: (d.data() as Record<string, unknown>).createdAt as Timestamp | null,
      }));
    },
    enabled: !!chainId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const bookingsOverviewQ = useQuery({
    queryKey: ["bookings", "overview", chainId],
    queryFn: () => chainAdminApi.chainAdminBookingsOverview({ chainId }),
    enabled: !!chainId,
    refetchInterval: ADMIN_REFETCH_INTERVAL,
  });

  const bookingPharmacies = (bookingsOverviewQ.data?.items ?? []).filter(
    (p) => p.hasBookingSetup && p.bookingPharmacyId,
  );

  const bookingQueries = useQueries({
    queries: bookingPharmacies.map((p) => ({
      queryKey: ["chain", chainId, "bookings", p.bookingPharmacyId, "dashboard", monthStartStr],
      queryFn: () =>
        chainAdminApi.chainAdminListBookings({
          chainId,
          bookingPharmacyId: p.bookingPharmacyId!,
          startDate: monthStartStr,
          endDate: todayStr,
        }),
      enabled: !!chainId,
      refetchInterval: ADMIN_REFETCH_INTERVAL,
    })),
  });

  const pharmacies = pharmaciesQ.data ?? [];
  const activePharmacies = pharmacies.filter((p) => p.status === "active").length;
  const suspendedPharmacies = pharmacies.filter((p) => p.status === "suspended").length;
  const pharmaciesWithBooking = bookingPharmacies.length;

  // adminListMemberships returns every role in the chain, so drop the
  // chain_admin / pharmacy_admin rows — this tile counts pharmacists only.
  const pharmacists = (pharmacistsQ.data?.items ?? []).filter(
    (m) => m.role === "pharmacist",
  );
  const activePharmacists = pharmacists.filter((m) => m.status === "active").length;
  const suspendedPharmacists = pharmacists.filter((m) => m.status === "suspended").length;

  const consultations = consultationsQ.data ?? [];
  const consultationsMonth = consultations.length;
  const consultationsWeek = consultations.filter(
    (c) => c.createdAt && c.createdAt.toMillis() >= weekTs.toMillis(),
  ).length;
  const consultationsToday = consultations.filter(
    (c) => c.createdAt && c.createdAt.toMillis() >= todayTs.toMillis(),
  ).length;

  const allBookings = bookingQueries.flatMap((q) => q.data?.items ?? []);
  const bookingsLoading =
    bookingsOverviewQ.isLoading || bookingQueries.some((q) => q.isLoading);
  const bookingsMonth = allBookings.length;
  const bookingsWeek = allBookings.filter((b) => isThisWeek(b.date, todayStr)).length;
  const bookingsToday = allBookings.filter((b) => b.date === todayStr).length;
  const upcomingBookings = bookingsOverviewQ.data?.totalUpcoming ?? 0;

  const lastUpdated = useMemo(() => {
    const timestamps = [
      pharmaciesQ.dataUpdatedAt,
      pharmacistsQ.dataUpdatedAt,
      consultationsQ.dataUpdatedAt,
      bookingsOverviewQ.dataUpdatedAt,
      ...bookingQueries.map((q) => q.dataUpdatedAt),
    ].filter(Boolean);
    return timestamps.length > 0 ? Math.max(...timestamps) : 0;
  }, [
    pharmaciesQ.dataUpdatedAt,
    pharmacistsQ.dataUpdatedAt,
    consultationsQ.dataUpdatedAt,
    bookingsOverviewQ.dataUpdatedAt,
    bookingQueries,
  ]);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="An overview of your chain's activity"
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {lastUpdated > 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mb: 2, textAlign: "right" }}
          >
            Last updated {new Date(lastUpdated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · refreshes every minute
          </Typography>
        )}

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
          Network
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 4,
          }}
        >
          <StatCard
            label="Active Pharmacies"
            value={activePharmacies}
            loading={pharmaciesQ.isLoading}
            subItems={[
              { label: "total", value: pharmacies.length },
              ...(suspendedPharmacies > 0
                ? [{ label: "suspended", value: suspendedPharmacies, color: "warning" as const }]
                : []),
            ]}
          />
          <StatCard
            label="Active Pharmacists"
            value={activePharmacists}
            loading={pharmacistsQ.isLoading}
            subItems={[
              { label: "total", value: pharmacists.length },
              ...(suspendedPharmacists > 0
                ? [{ label: "suspended", value: suspendedPharmacists, color: "warning" as const }]
                : []),
            ]}
          />
          <StatCard
            label="Pharmacies with Booking"
            value={bookingsOverviewQ.isLoading ? "—" : pharmaciesWithBooking}
            loading={bookingsOverviewQ.isLoading}
            description="Online booking enabled"
          />
          <StatCard
            label="Upcoming Bookings"
            value={bookingsOverviewQ.isLoading ? "—" : upcomingBookings}
            loading={bookingsOverviewQ.isLoading}
            description="Confirmed & pending"
          />
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
          Activity this month
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <StatCard
            label="Bookings — Today"
            value={bookingsLoading ? "—" : bookingsToday}
            loading={bookingsLoading}
          />
          <StatCard
            label="Bookings — This Week"
            value={bookingsLoading ? "—" : bookingsWeek}
            loading={bookingsLoading}
          />
          <StatCard
            label="Bookings — This Month"
            value={bookingsLoading ? "—" : bookingsMonth}
            loading={bookingsLoading}
          />
          <StatCard
            label="Consultations — Today"
            value={consultationsQ.isLoading ? "—" : consultationsToday}
            loading={consultationsQ.isLoading}
          />
          <StatCard
            label="Consultations — This Week"
            value={consultationsQ.isLoading ? "—" : consultationsWeek}
            loading={consultationsQ.isLoading}
          />
          <StatCard
            label="Consultations — This Month"
            value={consultationsQ.isLoading ? "—" : consultationsMonth}
            loading={consultationsQ.isLoading}
          />
        </Box>
      </Box>
    </>
  );
}
